import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {
  getContactsMadeByUser,
  sendMessage,
  getAllMessages,
  createChatRoom,
  uploadMultipleFile,
  getUserChatRooms,
  getAllInvites,
  updateInvites,
  CHAT_WEBSOCKET_URL,
} from "../../services/api";
import {
  ContactDetails,
  ChatRequestDto,
  MessageResponseDto,
  CreateChatRoomRequest,
  ChatRoom,
  FileMetadataDto,
  ContactsResponseDto,
  DetailedContactInviteResponseDto,
  ContactInvitesResponseDto,
  LastMessageDetail,
  ChatRoomsResponseDto,
  ProfileId,
} from "../../types/types";
import { toast } from "../../utils/toast";
import i18n from "i18next";
import { getDealStatusPreview } from "../../utils/chat/dealStatusMessage";

const WEBSOCKET_URL = CHAT_WEBSOCKET_URL;

const translateChat = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, { ns: "chat", ...options }) as string;

/** Deal status messages show a short translated text instead of the raw string. */
const toPreviewText = (message: string) =>
  getDealStatusPreview(message, translateChat) ?? message;

export const useChat = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const userId = searchParams.get("userId");
  const groupId = searchParams.get("groupId");
  const navigate = useNavigate();

  const [receiverUsername, setReceiverUsername] = useState("");

  const [receiverFirstNameAndLastName, setReceiverFirstNameAndLastName] =
    useState("");

  const [contacts, setContacts] = useState<ContactsResponseDto>();
  const [invites, setInvites] = useState<ContactInvitesResponseDto>();
  const [selectedUser, setSelectedUser] = useState<ContactDetails | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ChatRoom | null>(null);
  const [selectedInvite, setSelectedInvite] =
    useState<DetailedContactInviteResponseDto | null>(null);
  const [chatRooms, setChatRooms] = useState<ChatRoomsResponseDto>();
  const [messages, setMessages] = useState<MessageResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [selectedChatType, setSelectedChatType] = useState<
    "INDIVIDUAL" | "GROUP" | "INVITE"
  >("INDIVIDUAL");
  const [isConnected, setIsConnected] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileMetadataList, setFileMetadataList] = useState<FileMetadataDto[]>(
    [],
  );
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupBio, setGroupBio] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<ProfileId[]>([]);
  const [groupImageFile, setGroupImageFile] = useState<File | null>(null);
  const [groupImageUrl, setGroupImageUrl] = useState("");
  const [groupImagePreview, setGroupImagePreview] = useState<string | null>(
    null,
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const stompClientRef = useRef<Client | null>(null);
  const selectedUserRef = useRef<ContactDetails | null>(null);
  const selectedGroupRef = useRef<ChatRoom | null>(null);
  const selectedInviteRef = useRef<DetailedContactInviteResponseDto | null>(
    null,
  );
  const selectedChatTypeRef = useRef<"INDIVIDUAL" | "GROUP" | "INVITE">(
    "INDIVIDUAL",
  );
  const groupSubscriptionRef = useRef<any>(null);
  // Track whether the initial data load has already completed so
  // subsequent URL changes (contact clicks) don't trigger a full re-fetch.
  const initializedRef = useRef(false);
  const contactsRef = useRef<ContactsResponseDto | undefined>(undefined);
  const chatRoomsRef = useRef<ChatRoomsResponseDto | undefined>(undefined);

  const getCurrentUser = () => {
    const userJson = localStorage.getItem("loginResponse");
    return userJson ? JSON.parse(userJson) : null;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const truncateMessage = (text: string, maxLength: number = 30) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-500";
      case "PENDING":
        return "bg-yellow-500";
      case "INACTIVE":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getLastMessageDisplay = (contact: ContactDetails) => {
    if (!contact.lastMessageDetail) {
      return "No messages yet";
    }
    const { message, profileDto } = contact?.lastMessageDetail;

    if (message != null && profileDto != null) {
      const senderName = `${profileDto.firstName} ${profileDto.lastName}`;
      const currentUser = getCurrentUser();

      const isFromCurrentUser = profileDto.id === currentUser?.id;
      const prefix = isFromCurrentUser ? "You: " : `${senderName}: `;

      return prefix + truncateMessage(toPreviewText(message || "No message content"), 25);
    } else {
      return "No message content";
    }
  };

  const getLastMessageDisplayForChatRoom = (
    lastMessage: LastMessageDetail,
  ): string => {
    if (!lastMessage || !lastMessage.profileDto) {
      return "No messages yet";
    }

    const { message, profileDto } = lastMessage;
    const senderName = `${profileDto.firstName} ${profileDto.lastName}`;
    const currentUser = getCurrentUser();

    const isFromCurrentUser = profileDto.id === currentUser?.id;
    const prefix = isFromCurrentUser ? "You: " : `${senderName}: `;

    return prefix + truncateMessage(toPreviewText(message || "No message content"), 25);
  };

  const isMessageFromCurrentUser = (msg: MessageResponseDto) => {
    const currentUser = getCurrentUser();
    return msg.sender.id === currentUser?.id;
  };

  const determineMessageType = (
    message: string,
    fileMetadataList: FileMetadataDto[],
  ) => {
    if (message.trim() && fileMetadataList.length > 0) {
      return "CONTENT_AND_FILE";
    } else if (fileMetadataList.length > 0) {
      return "FILE";
    } else {
      return "CONTENT";
    }
  };
  const fetchContactsQuietly = useCallback(async () => {
    try {
      const data = await getContactsMadeByUser();
      setContacts(data);
      contactsRef.current = data; // keep ref in sync

      if (selectedUserRef.current) {
        const updatedSelectedUser = data.contactDetailsResponseDtos.find(
          (c) => c.profileDto.id === selectedUserRef.current?.profileDto.id,
        );
        if (updatedSelectedUser) {
          setSelectedUser(updatedSelectedUser);
        }
      }
    } catch (error) {
      console.error("Error fetching contacts quietly:", error);
    }
  }, []);

  const fetchInvitesQuietly = useCallback(async () => {
    try {
      const data = await getAllInvites();
      setInvites(data);

      if (selectedInviteRef.current) {
        const updatedSelectedInvite = data.inviteResponseDtos.find(
          (i) => i.contactedBy.id === selectedInviteRef.current?.contactedBy.id,
        );
        if (updatedSelectedInvite) {
          setSelectedInvite(updatedSelectedInvite);
        }
      }
    } catch (error) {
      console.error("Error fetching contacts quietly:", error);
    }
  }, []);

  const fetchChatRoomsQuietly = useCallback(async () => {
    try {
      const data = await getUserChatRooms();
      setChatRooms(data);
      chatRoomsRef.current = data; // keep ref in sync
    } catch (error) {
      console.error("Error fetching chat rooms quietly:", error);
    }
  }, []);

  const fetchMessages = useCallback(
    async (
      receiverId: string | number,
      chatType: "INDIVIDUAL" | "GROUP" = "INDIVIDUAL",
    ) => {
      try {
        setFetchingMessages(true);
        const response = await getAllMessages(receiverId, chatType);
        setMessages(response || []);
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        setMessages([]);
      } finally {
        setFetchingMessages(false);
      }
    },
    [],
  );

  const fetchGroupMessages = useCallback(async (groupId: number) => {
    try {
      setFetchingMessages(true);

      const response = await getAllMessages(groupId, "GROUP");

      if (response && Array.isArray(response)) {
        setMessages(response);
      } else {
        setMessages([]);
      }

      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      setMessages([]);
    } finally {
      setFetchingMessages(false);
    }
  }, []);

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles(files);
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return [];

    try {
      setUploadingFiles(true);
      const uploadedMetadata = await uploadMultipleFile(selectedFiles);
      setFileMetadataList(uploadedMetadata);
      return uploadedMetadata;
    } catch (error) {
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileMetadataList((prev) => prev.filter((_, i) => i !== index));
  };

  const clearFileSelection = () => {
    setSelectedFiles([]);
    setFileMetadataList([]);
  };

  const handleAcceptInvite = async (inviteId: number) => {
    // The Accept button now lives on each row of the "All request" list, so the
    // invite being accepted is not necessarily the one open on the right side.
    const invite =
      selectedInviteRef.current?.id === inviteId
        ? selectedInviteRef.current
        : (invites?.inviteResponseDtos?.find((i) => i.id === inviteId) ??
          selectedInviteRef.current);
    try {
      await updateInvites(inviteId, true);

      await Promise.all([
        fetchContactsQuietly(),
        fetchChatRoomsQuietly(),
        fetchInvitesQuietly(),
      ]);

      const updatedInvites = await getAllInvites();
      setInvites(updatedInvites);

      const updatedContact = contacts?.contactDetailsResponseDtos.find(
        (c) => c.profileDto.id === invite?.contacted.id,
      );

      if (updatedContact) {
        setSelectedUser(updatedContact);

        await sendMessage({
          receiverId: updatedContact.profileDto.id,
          message: `Hi ${invite?.contacted.firstName} ${invite?.contacted.lastName}! I would like to contact you regarding your opportunity for ${
            (invite?.opportunityCardResponseDto as any)?.formData?.details
              ?.sport || invite?.readyRosterCardResponseDto?.sport
          } with a purse of ${
            invite?.opportunityCardResponseDto?.formData?.core?.baseAmount ||
            invite?.readyRosterCardResponseDto?.purse
          }.`,
          chatType: "INDIVIDUAL",
          type: "CONTENT",
        });

        await Promise.all([
          fetchInvitesQuietly(),
          fetchContactsQuietly(),
          fetchChatRoomsQuietly(),
        ]);
      }

      setSelectedInvite(null);
    } catch (error) {
      console.error("Error accepting invite:", error);
    }
  };

  const handleDeclineInvite = async (inviteId: number) => {
    try {
      await updateInvites(inviteId, false);
      await Promise.all([
        fetchContactsQuietly(),
        fetchChatRoomsQuietly(),
        fetchInvitesQuietly(),
      ]);
      const updatedInvites = await getAllInvites();
      setInvites(updatedInvites);
      setSelectedInvite(null);
      setSelectedChatType("INDIVIDUAL");
      navigate("/chat");
    } catch (error) {
      console.error("Error declining invite:", error);
    }
  };

  const handleSelectInvite = (invite: DetailedContactInviteResponseDto | any) => {
    if (!invite) {
      setSelectedInvite(null);
      navigate('/chat');
      return;
    }
    setSelectedInvite(invite);
    setReceiverUsername(invite.contacted.id.toString());
    setReceiverFirstNameAndLastName(
      `${invite.contacted.firstName} ${invite.contacted.lastName}`,
    );
    setSelectedChatType("INVITE");
    setSelectedUser(null);
    setSelectedGroup(null);
    setMessages([]);
    setMessage("");
    clearFileSelection();
    navigate("/chat");
  };

  const handleWebSocketGroupMessage = useCallback(
    async (newMessage: MessageResponseDto) => {
      const currentSelectedGroup = selectedGroupRef.current;
      const messageGroupId = Number(newMessage?.receiver?.id);

      if (currentSelectedGroup && currentSelectedGroup.id === messageGroupId) {
        await fetchGroupMessages(currentSelectedGroup.id);
        await Promise.all([
          fetchContactsQuietly(),
          fetchChatRoomsQuietly(),
          fetchInvitesQuietly(),
        ]);
      } else {
        await Promise.all([
          fetchContactsQuietly(),
          fetchChatRoomsQuietly(),
          fetchInvitesQuietly(),
        ]);

        if (!currentSelectedGroup) {
          return;
        }

        console.log("Message is for different group, only chat rooms updated");
      }
    },
    [
      fetchGroupMessages,
      fetchChatRoomsQuietly,
      fetchContactsQuietly,
      fetchInvitesQuietly,
    ],
  );

  const subscribeToGroup = useCallback(
    (groupId: number) => {
      const client = stompClientRef.current;
      if (!client || !client.connected) {
        return;
      }

      try {
        if (groupSubscriptionRef.current) {
          groupSubscriptionRef.current.unsubscribe();
          groupSubscriptionRef.current = null;
        }

        const subscription = client.subscribe(
          `/group/${groupId}`,
          (message) => {
            try {
              const groupMessage: MessageResponseDto = JSON.parse(message.body);
              handleWebSocketGroupMessage(groupMessage);
            } catch (error) {
              console.error("Error parsing group message:", error);
            }
          },
        );

        groupSubscriptionRef.current = subscription;
      } catch (error) {
        console.error("Error subscribing to group:", error);
      }
    },
    [handleWebSocketGroupMessage],
  );

  const unsubscribeFromGroup = useCallback(() => {
    if (groupSubscriptionRef.current) {
      try {
        groupSubscriptionRef.current.unsubscribe();
        groupSubscriptionRef.current = null;
      } catch (error) {
        console.error("Error unsubscribing from group:", error);
      }
    }
  }, []);

  const handleGroupImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadMultipleFile([file]);
      setGroupImagePreview(URL.createObjectURL(file));
      setGroupImageUrl(url[0].fileUrl);
    } catch (error) {}
  };

  const handleCreateGroup = async () => {
    const payload: CreateChatRoomRequest = {
      name: groupName,
      bio: groupBio || "",
      profileImage: groupImageUrl,
      memberIds: selectedMemberIds,
    };

    try {
      const newChatRoom = await createChatRoom(payload);
      setChatRooms((prev) => {
        if (!prev) {
          return {
            contactedProfilesCount: 0,
            chatRoomResponseDtos: [newChatRoom],
          };
        }

        return {
          ...prev,
          chatRoomResponseDtos: [...prev.chatRoomResponseDtos, newChatRoom],
        };
      });

      setGroupName("");
      setGroupBio("");
      setSelectedMemberIds([]);
      setGroupImageFile(null);
      setGroupImageUrl("");
      setGroupImagePreview(null);
      setShowGroupModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to create a group");
    } finally {
      await fetchChatRoomsQuietly();
    }
  };

  const handleSelectContact = (user: ContactDetails | any) => {
    if (!user) {
      setSelectedUser(null);
      navigate('/chat');
      return;
    }
    if (selectedUser?.profileDto?.id === user?.profileDto?.id) {
      return;
    }

    unsubscribeFromGroup();
    setSelectedUser(user);
    setSelectedInvite(null);
    setSelectedChatType("INDIVIDUAL");
    setSelectedGroup(null);
    setMessages([]);
    setMessage("");
    clearFileSelection();
    navigate(`/chat?userId=${user.profileDto.id}`);
    setTimeout(() => scrollToBottom(), 200);
  };

  const handleSelectGroup = (group: ChatRoom) => {
    if (selectedGroup?.id === group.id) {
      return;
    }
    fetchChatRoomsQuietly();
    setSelectedGroup(group);
    setSelectedInvite(null);
    setSelectedChatType("GROUP");
    setSelectedUser(null);
    setMessages([]);
    setMessage("");
    clearFileSelection();

    if (isConnected) {
      subscribeToGroup(group.id);
    }

    setTimeout(() => scrollToBottom(), 200);
  };

  const handleSendMessage = async () => {
    if ((!message.trim() && selectedFiles.length === 0) || sending) return;

    try {
      setSending(true);
      let uploadedMetadata = fileMetadataList;

      if (selectedFiles.length > 0 && fileMetadataList.length === 0) {
        uploadedMetadata = await handleFileUpload();
      }

      if (selectedUser) {
        const payload: ChatRequestDto = {
          message: message.trim(),
          fileMetadataList: uploadedMetadata,
          type: determineMessageType(message.trim(), uploadedMetadata),
          chatType: "INDIVIDUAL",
          receiverId: selectedUser.profileDto.id,
        };

        try {
          await sendMessage(payload);
          await fetchMessages(selectedUser.profileDto.id, "INDIVIDUAL");
          await fetchContactsQuietly();
        } catch (sendError) {
          console.error("Error sending individual message:", sendError);
        }
      } else if (selectedGroup) {
        const payload: ChatRequestDto = {
          message: message.trim(),
          fileMetadataList: uploadedMetadata,
          type: determineMessageType(message.trim(), uploadedMetadata),
          chatType: "GROUP",
          receiverId: selectedGroup.id,
        };

        try {
          await sendMessage(payload);
        } catch (sendError) {
          await fetchChatRoomsQuietly();
          await fetchContactsQuietly();
          await fetchInvitesQuietly();
          await fetchGroupMessages(selectedGroup.id);
        }
      }

      setMessage("");
      clearFileSelection();
    } catch (error) {
      console.error("Critical error in handleSendMessage:", error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleWebSocketMessage = useCallback(
    async (newMessage: MessageResponseDto) => {
      const currentSelectedUser = selectedUserRef.current;
      const currentUser = getCurrentUser();

      if (!currentSelectedUser) {
        await Promise.all([
          fetchContactsQuietly(),
          fetchChatRoomsQuietly(),
          fetchInvitesQuietly(),
        ]);
        return;
      } else {
        await fetchMessages(currentSelectedUser.profileDto.id, "INDIVIDUAL");
        await Promise.all([
          fetchContactsQuietly(),
          fetchChatRoomsQuietly(),
          fetchInvitesQuietly(),
        ]);
      }
    },
    [
      fetchMessages,
      fetchContactsQuietly,
      fetchChatRoomsQuietly,
      fetchInvitesQuietly,
    ],
  );

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);

  useEffect(() => {
    selectedInviteRef.current = selectedInvite;
  }, [selectedInvite]);

  useEffect(() => {
    selectedChatTypeRef.current = selectedChatType;
  }, [selectedChatType]);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser?.id) {
      return;
    }

    const client = new Client({
      webSocketFactory: () => new SockJS(WEBSOCKET_URL),
      connectHeaders: {
        Authorization: `Bearer ${currentUser.token || ""}`,
      },
      debug: (str) => {
        console.log("STOMP Debug:", str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = (frame) => {
      setIsConnected(true);

      client.subscribe(`/specific/chat/user/${currentUser.id}`, (message) => {
        try {
          const chatMessage: MessageResponseDto = JSON.parse(message.body);
          handleWebSocketMessage(chatMessage);
        } catch (error) {
          console.error("Error parsing specific chat message:", error);
        }
      });

      client.subscribe(`/all/chat/user/${currentUser.id}`, (message) => {
        try {
          const chatMessage: MessageResponseDto = JSON.parse(message.body);
          if (chatMessage.chatType === "GROUP" || isGroupMessage(chatMessage)) {
            handleWebSocketGroupMessage(chatMessage);
          } else {
            handleWebSocketMessage(chatMessage);
          }
        } catch (error) {
          console.error("Error parsing all chat message:", error);
        }
      });

      client.subscribe(`/user/${currentUser.id}/reply`, async (message) => {
        try {
          const notification = JSON.parse(message.body);

          if (selectedUserRef.current) {
            await fetchMessages(selectedUserRef.current.profileDto.id, "INDIVIDUAL");
          } else if (selectedGroupRef.current) {
            await fetchGroupMessages(selectedGroupRef.current.id);
          }

          await Promise.all([
            fetchContactsQuietly(),
            fetchChatRoomsQuietly(),
            fetchInvitesQuietly(),
          ]);
        } catch (error) {
          console.error("Error parsing notification:", error);
        }
      });

      if (selectedGroupRef.current) {
        subscribeToGroup(selectedGroupRef.current.id);
      }
    };

    client.onStompError = (frame) => {
      setIsConnected(false);
    };

    client.onWebSocketError = (error) => {
      setIsConnected(false);
    };

    client.onDisconnect = () => {
      setIsConnected(false);
      groupSubscriptionRef.current = null;
    };

    client.activate();
    stompClientRef.current = client;

    return () => {
      unsubscribeFromGroup();
      if (stompClientRef.current) {
        stompClientRef.current.deactivate();
        setIsConnected(false);
      }
    };
  }, [
    handleWebSocketMessage,
    handleWebSocketGroupMessage,
    subscribeToGroup,
    unsubscribeFromGroup,
  ]);

  useEffect(() => {
    if (isConnected && selectedGroup && stompClientRef.current?.connected) {
      subscribeToGroup(selectedGroup.id);
    } else if (!selectedGroup) {
      unsubscribeFromGroup();
    }
  }, [isConnected, selectedGroup, subscribeToGroup, unsubscribeFromGroup]);

  const isGroupMessage = (message: MessageResponseDto): boolean => {
    return (
      message.chatType === "GROUP" || (message.receiver && !message.receiver.id)
    );
  };
  const subscribeToAllUserGroups = useCallback(() => {
    const client = stompClientRef.current;
    if (!client || !client.connected || !chatRooms?.chatRoomResponseDtos) {
      return;
    }

    chatRooms.chatRoomResponseDtos.forEach((group: ChatRoom) => {
      client.subscribe(`/group/${group.id}`, async (message) => {
        try {
          const groupMessage: MessageResponseDto = JSON.parse(message.body);
          if (selectedGroupRef.current?.id === group.id) {
            await fetchGroupMessages(group.id);
          }
          await Promise.all([
            fetchContactsQuietly(),
            fetchChatRoomsQuietly(),
            fetchInvitesQuietly(),
          ]);
        } catch (error) {
          console.error("Error parsing group message:", error);
        }
      });
    });
  }, [
    chatRooms,
    fetchGroupMessages,
    fetchContactsQuietly,
    fetchChatRoomsQuietly,
    fetchInvitesQuietly,
  ]);

  useEffect(() => {
    if (isConnected && chatRooms?.chatRoomResponseDtos) {
      subscribeToAllUserGroups();
    }
  }, [isConnected, chatRooms, subscribeToAllUserGroups]);

  // ─── INITIAL LOAD (runs once on mount) ───────────────────────────────────
  useEffect(() => {
    const fetchContactsAndGroups = async () => {
      try {
        setLoading(true);
        const [contactsData, groupsData, allInvites] = await Promise.all([
          getContactsMadeByUser(),
          getUserChatRooms(),
          getAllInvites(),
        ]);
        setContacts(contactsData);
        setChatRooms(groupsData);
        setInvites(allInvites);
        // Keep refs in sync so URL-navigation effect can read them synchronously
        contactsRef.current = contactsData;
        chatRoomsRef.current = groupsData;

        if (location.pathname === "/chat/invites") {
          setSelectedChatType("INVITE");
          setSelectedUser(null);
          setSelectedGroup(null);
          setSelectedInvite(null);
          setMessages([]);
          navigate("/chat/invites");
        } else if (userId) {
          const matchUser = contactsData.contactDetailsResponseDtos.find(
            (c) => c.profileDto.id?.toString() === userId,
          );
          if (matchUser) {
            setSelectedUser(matchUser);
            setSelectedInvite(null);
            setSelectedChatType("INDIVIDUAL");
          }
        } else if (groupId) {
          const matchGroup = groupsData.chatRoomResponseDtos.find(
            (g: ChatRoom) => g.id === parseInt(groupId),
          );
          if (matchGroup) {
            setSelectedGroup(matchGroup);
            setSelectedInvite(null);
            setSelectedChatType("GROUP");
          }
        } else {
          setSelectedChatType("INDIVIDUAL");
          setSelectedUser(null);
          setSelectedGroup(null);
          setSelectedInvite(null);
          setMessages([]);
        }

        initializedRef.current = true;
      } catch (err) {
        console.error("Error fetching contacts or groups:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactsAndGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← run only once on mount

  // ─── URL-DRIVEN NAVIGATION (no re-fetch, no loading) ─────────────────────
  // Fires when the URL changes AFTER the initial load, e.g. when an
  // external link navigates to /chat?userId=XYZ while the component is already mounted.
  useEffect(() => {
    if (!initializedRef.current) return; // skip during the initial load
    const currentContacts = contactsRef.current;
    const currentChatRooms = chatRoomsRef.current;

    if (location.pathname === "/chat/invites") {
      setSelectedChatType("INVITE");
      setSelectedUser(null);
      setSelectedGroup(null);
      setSelectedInvite(null);
      setMessages([]);
    } else if (userId && currentContacts) {
      const matchUser = currentContacts.contactDetailsResponseDtos.find(
        (c) => c.profileDto.id?.toString() === userId,
      );
      if (matchUser && matchUser.profileDto.id !== selectedUserRef.current?.profileDto.id) {
        setSelectedUser(matchUser);
        setSelectedInvite(null);
        setSelectedChatType("INDIVIDUAL");
      }
    } else if (groupId && currentChatRooms) {
      const matchGroup = currentChatRooms.chatRoomResponseDtos.find(
        (g: ChatRoom) => g.id === parseInt(groupId),
      );
      if (matchGroup && matchGroup.id !== selectedGroupRef.current?.id) {
        setSelectedGroup(matchGroup);
        setSelectedInvite(null);
        setSelectedChatType("GROUP");
      }
    }
  }, [userId, groupId, location.pathname]);

  const selectedUserId = selectedUser?.profileDto.id;
  useEffect(() => {
    if (selectedUserId === undefined) return;
    fetchMessages(selectedUserId, "INDIVIDUAL").then(() => {
      fetchContactsQuietly();
    });
  }, [selectedUserId, fetchMessages, fetchContactsQuietly]);

  const selectedGroupId = selectedGroup?.id;
  useEffect(() => {
    if (selectedGroupId === undefined) return;
    fetchGroupMessages(selectedGroupId).then(() => {
      fetchChatRoomsQuietly();
    });
  }, [selectedGroupId, fetchGroupMessages, fetchChatRoomsQuietly]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return {
    contacts,
    invites,
    setInvites,
    selectedUser,
    selectedGroup,
    selectedChatType,
    setSelectedChatType,
    selectedInvite,
    chatRooms,
    setChatRooms,
    messages,
    loading,
    message,
    sending,
    fetchingMessages,
    showGroupModal,
    groupName,
    groupBio,
    selectedMemberIds,
    groupImageFile,
    groupImageUrl,
    groupImagePreview,
    selectedFiles,
    fileMetadataList,
    uploadingFiles,
    messagesEndRef,
    fetchMessages,
    fetchGroupMessages,
    handleGroupImageUpload,
    handleCreateGroup,
    handleSelectContact,
    handleSelectGroup,
    handleSelectInvite,
    handleAcceptInvite,
    handleDeclineInvite,
    handleSendMessage,
    handleKeyPress,
    handleFileSelect,
    handleFileUpload,
    removeFile,
    clearFileSelection,
    getStatusColor,
    getLastMessageDisplay,
    getLastMessageDisplayForChatRoom,
    isMessageFromCurrentUser,
    determineMessageType,
    setMessage,
    setShowGroupModal,
    setGroupName,
    setGroupBio,
    setSelectedMemberIds,
    setGroupImageFile,
    setGroupImageUrl,
    setGroupImagePreview,
    setSelectedFiles,
    setFileMetadataList,
    setSelectedGroup,
    sendMessage,
  };
};
